import { useContext, useState, useSyncExternalStore } from 'react';
import { FieldValues } from 'react-hook-form';
import { showModal } from '@/utils/modal.utils';
import { TCustomQueryProps } from '@/type/hooks.types';
import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { ErrorResponse } from '@/utils/axios.http';
import { AllSelectedTableItemsContext } from '@/providers/AllSelectedTableItemsProvider';
import { TFunction } from 'i18next';

// Track the last action (deactivate or delete) that opened the info modal,
// so all hook instances on the page show the matching message.
const actionStore = {
  value: 'deactivate',
  listeners: new Set<() => void>(),
};
const subscribeAction = (cb: () => void) => {
  actionStore.listeners.add(cb);
  return () => {
    actionStore.listeners.delete(cb);
  };
};
const getActionSnapshot = () => actionStore.value;
const setAction = (value: 'deactivate' | 'delete') => {
  actionStore.value = value;
  actionStore.listeners.forEach((cb) => cb());
};

interface useDeactivateFieldProps<TSchema extends FieldValues> {
  linkedFieldNames?: string[];
  t?: TFunction<undefined>;
  actionType?: 'deactivate' | 'delete';
  confirmModalId?: string;
  useUpdateData?: (
    objects?: Partial<TCustomQueryProps>,
  ) => UseMutationResult<
    AxiosResponse<TSchema[], unknown>,
    ErrorResponse,
    TSchema[],
    unknown
  >;
  useDeleteData?: (
    objects?: Partial<TCustomQueryProps>,
  ) => UseMutationResult<
    AxiosResponse<unknown, unknown>,
    ErrorResponse,
    string[],
    unknown
  >;
}

export const useDeactivateField = <TSchema extends FieldValues>({
  t,
  linkedFieldNames,
  actionType = 'deactivate',
  confirmModalId = '#deactivation-confirm-modal',
  useUpdateData,
  useDeleteData,
}: useDeactivateFieldProps<TSchema>) => {
  const [selectedObjects, setSelectedObjects] = useState<TSchema[]>([]);
  const action = useSyncExternalStore(subscribeAction, getActionSnapshot);
  const { setAllTableItemsSelected, setSelectedTableData } = useContext(
    AllSelectedTableItemsContext,
  );

  const { mutate: mutateDeactivate, isPending: pendingDeactivate } =
    useUpdateData?.({ t }) || { mutate: () => {}, isPending: false };

  const { mutate: mutateDelete, isPending: pendingDelete } = useDeleteData?.({
    t,
  }) || { mutate: () => {}, isPending: false };

  const handleDeactivate = (objects: TSchema[]) => {
    setAction(actionType);
    setSelectedObjects(objects);

    const linkedFieldExists = objects?.some(
      (item) => item?.[linkedFieldNames?.[0] as keyof TSchema]?.length > 0,
    );

    if (objects.length > 1) {
      showModal(confirmModalId);
    } else if (objects.length === 1) {
      if (linkedFieldExists) {
        showModal('#information-modal');
      } else {
        showModal(confirmModalId);
      }
    }
  };

  const onConfirmDeactivate = () => {
    if (selectedObjects.length > 0) {
      if (actionType === 'delete') {
        // Delete data - skip items with linked fields, then extract IDs
        const ids = selectedObjects
          .filter(
            (item) =>
              !linkedFieldNames ||
              linkedFieldNames?.length === 0 ||
              item[linkedFieldNames?.[0]]?.length === 0,
          )
          .map((obj) => obj.id)
          .filter(Boolean);
        mutateDelete(ids);
      } else {
        // Deactivate data (default behavior)
        let deactivatedData = [];

        for (const item of selectedObjects) {
          if (
            !linkedFieldNames ||
            linkedFieldNames?.length === 0 ||
            item[linkedFieldNames?.[0]]?.length === 0
          ) {
            deactivatedData = [
              ...deactivatedData,
              {
                ...item,
                active: false,
              },
            ];
          }
        }

        mutateDeactivate(deactivatedData);
      }

      // Reset selection after action
      setAllTableItemsSelected(false);
      setSelectedTableData([]);
      setSelectedObjects([]);
    }
  };

  return {
    numberOfItemsToDeactivate: selectedObjects.length,
    pendingDeactivate:
      actionType === 'delete' ? pendingDelete : pendingDeactivate,
    handleDeactivate,
    onConfirmDeactivate,
    action,
  };
};
